import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {PageHeader} from "../components/layout/PageHeader";
import {Button, Col, Form, Input, Popconfirm, Row, Spin, Switch, Select} from "antd";
import {
    deleteTemplateGuid,
    getTemplateGuid,
    TemplateParameterDrag,
    templateParameterDragSubject,
    templateParametersSubject,
    updateTemplateGuid,
    useTemplateGuid,
    useTemplateGuidDelete,
    useTemplateParameterDrag,
    useTemplateParameters
} from "../hooks/template";
import {useHistory} from "react-router-dom";
import {TemplateCreateEditDto, TemplateParameterDto} from "../generated-api";
import {Menu, PlusCircle, Trash2, XCircle, Type, Calendar} from "react-feather";
import {BackButton} from "../components/layout/BackButton";
import {findParent} from "../utils/dom";
import {TEMPLATE_PARAM_TEXT, TEMPLATE_PARAM_DATE} from "../constants/template";

export function TemplatePage({match}: any) {
    const {templateId} = (match || {}).params || {};
    const {t} = useTranslation();
    const history = useHistory();
    const templateResponse = useTemplateGuid();
    const template = (templateResponse || {}).data;
    const businessSector = (template || {}).businessSector || {};
    const templateDeleteResponse = useTemplateGuidDelete();
    useEffect(() => {
        setParameters([]);
        if (templateId) {
            getTemplateGuid(templateId, true).then(template => {
                setParameters(template.templateParameters);
            }).catch(error => {
                console.error(error);
                history.push('/templates');
            });
        } else {
            history.push('/templates');
        }
    }, [templateId]);
    useEffect(() => {
        window.document.addEventListener('pointermove', mouseMove);
        window.document.addEventListener('pointerup', mouseUp);
        return () => {
            window.document.removeEventListener('pointermove', mouseMove);
            window.document.removeEventListener('pointerup', mouseUp);
        }
    }, [true]);
    const [newParameter, setNewParameter] = useState({name: '', type: 1, required: true, visibleInList: false});
    const parameters = useTemplateParameters();
    const setParameters = (parameters: TemplateParameterDto[]) => templateParametersSubject.next(parameters);
    const drag = useTemplateParameterDrag();
    const setDrag = (data: TemplateParameterDrag) => templateParameterDragSubject.next(data);

    const validateMessages = {
        required: t('error.required'),
        string: {
            max: t('error.string.max')
        }
    };

    const onFinish = (values: TemplateCreateEditDto): void => {
        values.parameters = parameters.map((item: TemplateParameterDto) => {
            return {
                id: item.id,
                name: item.name,
                type: item.type,
                required: !!item.required,
                visibleInList: !!item.visibleInList,
                order: item.order
            } as any;
        });
        if (newParameter && newParameter.name) {
            values.parameters.push({...newParameter, order: values.parameters.length + 1} as any);
        }
        updateTemplateGuid(templateId, values).then(() => {
            history.push('/templates');
        }).catch(console.error);
    };

    const onDelete = (): void => {
        deleteTemplateGuid(templateId).then(() => {
            history.push('/templates');
        }).catch(console.error);
    };

    const addParameter = (): void => {
        setParameters([...parameters, {...newParameter, order: parameters.length + 1} as TemplateParameterDto]);
        setNewParameter({name: '', type: 1, required: true, visibleInList: false});
    };

    const setParameterValue = (index: number, value: string): void => {
        const changed = [...parameters];
        changed[index].name = value;
        setParameters(changed);
    };

    const setParameterRequired = (index: number, required: boolean): void => {
        const changed = [...parameters];
        changed[index].required = required;
        setParameters(changed);
    };

    const setParameterVisibleInList = (index: number, visibleInList: boolean): void => {
        const changed = [...parameters];
        changed[index].visibleInList = visibleInList;
        setParameters(changed);
    };

    const removeParameter = (index: number): void => {
        const changed = [...parameters];
        changed.splice(index, 1);
        setParameters(changed);
    };

    const mouseDown = (event: any, item: any, index: number): void => {
        event.nativeEvent.preventDefault();
        event.nativeEvent.stopPropagation();
        const startY = event.pageY;
        const element = findParent(event.target, (element: any) => {
            return String(element.className).indexOf('drag-row') !== -1;
        });
        const height = element && (element.clientHeight + 16);
        setDrag(new TemplateParameterDrag(item, index, startY, height));
    };

    const mouseUp = (): void => setDrag(new TemplateParameterDrag());

    const mouseMove = (event: any): void => {
        event.preventDefault();
        event.stopPropagation();
        let drag = templateParameterDragSubject.getValue();
        let parameters = templateParametersSubject.getValue();
        if (drag && drag.item) {
            const offset = event.pageY - drag.startY;
            const {height} = drag;
            drag = {...drag, offset};
            if ((offset >= height && drag.index < (parameters.length - 1)) || (offset <= -height && drag.index > 0)) {
                let index = drag.index + 1;
                if (offset <= -height) {
                    index = drag.index - 1;
                }
                const otherParameter = parameters[index];
                if (otherParameter) {
                    const tmpOrder = drag.item.order;
                    drag.item.order = otherParameter.order;
                    otherParameter.order = tmpOrder;
                    const tmpParameter = parameters[drag.index];
                    parameters[drag.index] = parameters[index];
                    parameters[index] = tmpParameter;
                    setParameters(parameters);
                    drag = {...drag, index, startY: event.pageY, offset: 0};
                }
            }
            setDrag(drag);
        }
    };

    return (
        <Spin spinning={templateResponse.loading || templateDeleteResponse.loading}>
            <PageHeader/>
            <div className={'top-block accent6-background'}>
                <div className={'flex-row'}>
                    <h1 className={'h1 flex-auto'}><BackButton backPath={'/templates'}/>{t('template.title')}</h1>
                    <Popconfirm placement="bottomRight"
                                className={'title-icon-button ant-typography ant-typography-danger cursor-pointer'}
                                title={t('template.deleteConfirmation')}
                                onConfirm={onDelete}
                                okButtonProps={{danger: true}}
                                okText={t('general.yes')}
                                cancelText={t('general.no')}>
                        <Trash2/>
                    </Popconfirm>
                </div>
            </div>
            <div className={'subpage-content'}>
                <Form name="template" key={templateResponse.loading}
                      onFinish={onFinish}
                      autoComplete={'off'}
                      labelCol={{span: 11}}
                      validateMessages={validateMessages}
                      initialValues={template}
                      validateTrigger={'onSubmit'}>
                    <Row className={'row-offset'}>
                        <Col span={24} md={18} lg={12} xl={8}>
                            <Form.Item label={t('template.name')} className={'label-left'}
                                       name={'name'} rules={[{required: true}, {max: 512}]}>
                                <Input/>
                            </Form.Item>
                        </Col>
                        <Col span={24} md={18} lg={12} xl={8}>
                            <Form.Item label={t('template.businessSector')} className={'label-left'}>
                                <Input readOnly value={businessSector.name}/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Form.Item>
                                <div className={'ant-form-item-label'}>
                                    <label>{t('template.parameters')}</label>
                                </div>
                                {parameters.map((item: any, index: number) =>
                                    <div key={item.id + '_' + index}
                                         className={'flex-row mb-1 drag-row ' + (drag.item && drag.index === index ? 'drag-active' : '')}
                                         style={{top: (drag.offset) + 'px'}}>
                                        <div className={'flex-auto'}>
                                            <Row>
                                                <Col xl={12} lg={12} md={24} sm={24} className={'flex-auto flex-row'}>
                                                    <div className={'flex-row'}>
                                                        <div className={'drag-trigger'}>
                                                            <Menu
                                                                onPointerDown={(event: any) => mouseDown(event, item, index)}/>
                                                        </div>
                                                        <div className="pt-05">
                                                            { item.type === TEMPLATE_PARAM_TEXT ? <Type /> : <Calendar />}
                                                            
                                                        </div>
                                                        <div className={'flex-auto'}>
                                                            <Input value={item.name} className={''}
                                                                   onChange={event => setParameterValue(index, event.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col xl={12} lg={12} md={24} sm={24} className={'flex-auto'}>
                                                    <div className={'flex-row'}>
                                                        <div className={'flex-auto form-item-checkbox-wrapper'}>
                                                            <label>
                                                                {t('template.visibleInList')}{' '}
                                                                <Switch checked={item.visibleInList}
                                                                        onChange={event => setParameterVisibleInList(index, event)}
                                                                />
                                                            </label>
                                                        </div>
                                                        <div className={'flex-auto form-item-checkbox-wrapper'}>
                                                            <label>
                                                                {t('template.required')}{' '}
                                                                <Switch checked={item.required}
                                                                        onChange={event => setParameterRequired(index, event)}
                                                                />
                                                            </label>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                        <div>
                                            <Button type={'link'} className={'form-item-icon-button'}
                                                    onClick={() => removeParameter(index)}>
                                                <XCircle className={'ant-typography ant-typography-danger'}/>
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                <div className={'flex-row pt-1'}>
                                    <div className={'flex-auto'}>
                                        <Row>
                                            <Col xl={12} lg={12} md={24} sm={24} className={'flex-auto flex-row'}>
                                                <Select className={'parameter-type-select'} value={newParameter.type} style={{width: "60px"}} placeholder={t('template.newParamterType')}
                                                    onChange={event => 
                                                        setNewParameter({
                                                            name: newParameter.name,
                                                            type: event,
                                                            visibleInList: newParameter.visibleInList,
                                                            required: newParameter.required
                                                    })}
                                                >
                                                    <Select.Option key={TEMPLATE_PARAM_TEXT} value={TEMPLATE_PARAM_TEXT} >
                                                        <Type/>
                                                    </Select.Option>
                                                    <Select.Option key={TEMPLATE_PARAM_DATE} value={TEMPLATE_PARAM_DATE}>
                                                        <Calendar/>
                                                    </Select.Option>
                                                </Select>
                                                <Input value={newParameter.name}
                                                       placeholder={t('template.newParameter')}
                                                       className={'flex-auto'}
                                                       onChange={event => setNewParameter({
                                                           name: event.target.value,
                                                           type: newParameter.type,
                                                           visibleInList: newParameter.visibleInList,
                                                           required: newParameter.required
                                                       })}
                                                />
                                            </Col>
                                            <Col xl={12} lg={12} md={24} sm={24} className={'flex-auto'}>
                                                <div className={'flex-row'}>
                                                    <div className={'flex-auto form-item-checkbox-wrapper'}>
                                                        <label>
                                                            {t('template.visibleInList')}{' '}
                                                            <Switch checked={newParameter.visibleInList}
                                                                    onChange={event => setNewParameter({
                                                                        name: newParameter.name,
                                                                        type: newParameter.type,
                                                                        visibleInList: event,
                                                                        required: newParameter.required
                                                                    })}
                                                            />
                                                        </label>
                                                    </div>
                                                    <div className={'flex-auto form-item-checkbox-wrapper'}>
                                                        <label>
                                                            {t('template.required')}{' '}
                                                            <Switch checked={newParameter.required}
                                                                    onChange={event => setNewParameter({
                                                                        name: newParameter.name,
                                                                        type: newParameter.type,
                                                                        visibleInList: newParameter.visibleInList,
                                                                        required: event
                                                                    })}
                                                            />
                                                        </label>
                                                    </div>
                                                    
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                    <div>
                                        <Button type={'link'} className={'form-item-icon-button'}
                                                disabled={!newParameter.name}
                                                onClick={addParameter}>
                                            <PlusCircle/>
                                        </Button>
                                    </div>
                                </div>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item className={'no-margin text-right'}>
                        <Button type="primary" className={'accent6-background text-white w-100'} htmlType="submit">
                            {t('template.save')}
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </Spin>
    )
}
